import { useMemo, useState } from 'react';
import {
  type FlowEdge,
  type FlowGraph,
  type FlowNode,
  type FlowProblem,
  type Palette,
} from '@whinerp/contracts/flow';
import { cx } from '../primitives/cx.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';
import { passthrough } from '../primitives/passthrough.js';

/**
 * FlowCanvas - one canvas, four palettes.
 *
 * An automation, a marketing journey, a chatbot script and an approval chain
 * are the same graph. Four canvases would become four sets of the same
 * problems solved differently: three of them would never learn what the fourth
 * discovered about a branch with nowhere to go.
 *
 * Two things it does that a picture of a graph does not:
 *
 * **It draws the path a run actually took**, on the nodes, with the branch it
 * chose. Reading a run log beside a static diagram means holding the route in
 * your head, and the whole reason somebody opens a run log is that they cannot.
 *
 * **It carries per-node counts.** How many people are sitting at each step is
 * the question a journey provokes, and a canvas that cannot answer it sends
 * people to a report to find out what their own screen is about.
 */

export interface NodeCount {
  nodeId: string;
  /** Already formatted. */
  count: string;
}

export interface ExecutedStep {
  nodeId: string;
  sequence: number;
  status: 'entered' | 'passed' | 'failed' | 'skipped';
  branch: string | null;
  message: string | null;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
}

export interface FlowCanvasLabels {
  palette: string;
  addStep: (label: string) => string;
  removeStep: (label: string) => string;
  /** The accessible name of the whole canvas. */
  canvas: string;
  /** On a node that a run entered. */
  step: (sequence: string) => string;
  tookBranch: (branch: string) => string;
  notReached: string;
  input: string;
  output: string;
  atThisStep: (count: string) => string;
  problems: string;
}

export interface FlowCanvasProps {
  graph: FlowGraph;
  palette: Palette;
  labels: FlowCanvasLabels;
  onChange?: ((graph: FlowGraph) => void) | undefined;
  /** Everything wrong with the graph, drawn on the node it is about. */
  problems?: readonly FlowProblem[];
  /** Per-node live counts. */
  counts?: readonly NodeCount[];
  /** The path a run took, drawn over the graph. */
  executed?: readonly ExecutedStep[];
  selectedNodeId?: string | null;
  onSelectNode?: ((nodeId: string | null) => void) | undefined;
}

const NODE_WIDTH = 200;
const NODE_HEIGHT = 72;

export function FlowCanvas({
  graph,
  palette,
  labels,
  onChange,
  problems,
  counts,
  executed,
  selectedNodeId,
  onSelectNode,
  ...rest
}: FlowCanvasProps & Record<string, unknown>): JSX.Element {
  const [open, setOpen] = useState<string | null>(null);

  const executedByNode = useMemo(() => {
    const map = new Map<string, ExecutedStep>();
    for (const step of executed ?? []) map.set(step.nodeId, step);
    return map;
  }, [executed]);

  const countByNode = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of counts ?? []) map.set(entry.nodeId, entry.count);
    return map;
  }, [counts]);

  const problemsByNode = useMemo(() => {
    const map = new Map<string, FlowProblem[]>();
    for (const problem of problems ?? []) {
      if (!problem.nodeId) continue;
      map.set(problem.nodeId, [...(map.get(problem.nodeId) ?? []), problem]);
    }
    return map;
  }, [problems]);

  /** An edge is on the executed path when both ends ran and the branch matches. */
  const edgeWasTaken = (edge: FlowEdge): boolean => {
    const from = executedByNode.get(edge.from);
    const to = executedByNode.get(edge.to);
    if (!from || !to) return false;
    if (to.sequence !== from.sequence + 1) return false;
    return edge.branch === null || edge.branch === from.branch;
  };

  const graphProblems = (problems ?? []).filter((problem) => !problem.nodeId);

  return (
    <div {...passthrough(rest)} data-component="FlowCanvas" className="flex w-full flex-col gap-2">
      {/* The palette. Data, not a component per flow kind. */}
      {onChange ? (
        <div data-flow-palette={palette.key} className="flex flex-wrap items-center gap-2">
          <span className="text-micro uppercase tracking-label text-ink-label">{labels.palette}</span>
          {palette.entries.map((entry) => (
            <button
              key={entry.type}
              type="button"
              data-palette-entry={entry.type}
              title={entry.summary}
              aria-label={labels.addStep(entry.label)}
              onClick={() => onChange(addNode(graph, entry.type, entry.kind, entry.label))}
              className="inline-flex h-touch items-center gap-1 rounded-control bg-sunken px-2 text-meta text-ink-600 hover:text-ink"
            >
              <Icon name={entry.icon} size="inline" decorative />
              {entry.label}
            </button>
          ))}
        </div>
      ) : null}

      {graphProblems.length > 0 ? (
        <ul role="alert" data-flow-problems className="flex flex-col gap-1">
          {graphProblems.map((problem) => (
            <li key={problem.message} className="flex items-center gap-1 text-dense text-danger-text">
              <Icon name="Warning" size="inline" decorative />
              {problem.message}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="w-full overflow-x-auto" data-scroll-container>
        <div
          role="group"
          aria-label={labels.canvas}
          className="relative min-h-[24rem] min-w-panel-wide"
          style={{ width: canvasWidth(graph), height: canvasHeight(graph) }}
        >
          {/* Edges first, so nodes sit over them. */}
          <svg
            aria-hidden
            className="absolute inset-0 h-full w-full"
            width={canvasWidth(graph)}
            height={canvasHeight(graph)}
          >
            {graph.edges.map((edge) => {
              const from = graph.nodes.find((node) => node.id === edge.from);
              const to = graph.nodes.find((node) => node.id === edge.to);
              if (!from || !to) return null;
              const taken = edgeWasTaken(edge);
              return (
                <line
                  key={edge.id}
                  data-flow-edge={edge.id}
                  data-taken={taken || undefined}
                  x1={from.x + NODE_WIDTH}
                  y1={from.y + NODE_HEIGHT / 2}
                  x2={to.x}
                  y2={to.y + NODE_HEIGHT / 2}
                  className={cx(
                    'stroke-2',
                    taken ? 'stroke-bronze' : 'stroke-rule',
                  )}
                />
              );
            })}
          </svg>

          {graph.nodes.map((node) => {
            const step = executedByNode.get(node.id);
            const nodeProblems = problemsByNode.get(node.id) ?? [];
            const count = countByNode.get(node.id);
            const isOpen = open === node.id;

            return (
              <div
                key={node.id}
                data-flow-node={node.id}
                data-node-kind={node.kind}
                // Three attributes rather than one class: the path a run took,
                // whether this node ran, and whether it failed are three facts
                // a reader asks separately.
                data-executed={step ? 'true' : undefined}
                data-step-status={step?.status}
                data-branch={step?.branch ?? undefined}
                className={cx(
                  'absolute flex flex-col gap-0.5 rounded-card border p-2',
                  selectedNodeId === node.id ? 'border-bronze' : 'border-rule',
                  step ? 'bg-bronze-tint' : 'bg-surface',
                  step?.status === 'failed' && 'border-danger bg-danger-tint',
                  nodeProblems.length > 0 && 'border-danger',
                )}
                style={{ left: node.x, top: node.y, width: NODE_WIDTH }}
              >
                <button
                  type="button"
                  data-select-node={node.id}
                  onClick={() => {
                    onSelectNode?.(node.id);
                    setOpen(isOpen ? null : node.id);
                  }}
                  className="flex items-center gap-1 text-start"
                >
                  <Icon name={iconFor(palette, node.type)} size="inline" decorative />
                  <span className="min-w-0 flex-1 truncate text-dense text-ink">{node.label}</span>
                  {/* The sequence number: a reader following a path needs the
                      order, not only the fact that a node ran. */}
                  {step ? (
                    <Num className="text-meta text-bronze-text">
                      {labels.step(String(step.sequence))}
                    </Num>
                  ) : null}
                </button>

                {count !== undefined ? (
                  <Num data-node-count={node.id} className="text-meta text-ink-meta">
                    {labels.atThisStep(count)}
                  </Num>
                ) : null}

                {step?.branch ? (
                  <span data-node-branch className="text-meta text-bronze-text">
                    {labels.tookBranch(step.branch)}
                  </span>
                ) : null}

                {executed && !step ? (
                  // Said, rather than left to be inferred from the absence of
                  // a highlight - which reads the same as a node that ran and
                  // did nothing.
                  <span data-not-reached className="text-meta text-ink-300">
                    {labels.notReached}
                  </span>
                ) : null}

                {nodeProblems.map((problem) => (
                  <span
                    key={problem.message}
                    data-node-problem={node.id}
                    className="text-meta text-danger-text"
                  >
                    {problem.message}
                  </span>
                ))}

                {/* Per-node input and output, in place. Opening a separate
                    panel to see what a node saw means losing the picture that
                    made the question worth asking. */}
                {isOpen && step ? (
                  <dl data-node-io={node.id} className="flex flex-col gap-0.5 text-meta">
                    <dt className="text-ink-label">{labels.input}</dt>
                    <dd className="whitespace-pre-wrap text-ink">{format(step.input)}</dd>
                    <dt className="text-ink-label">{labels.output}</dt>
                    <dd className="whitespace-pre-wrap text-ink">{format(step.output)}</dd>
                    {step.message ? <dd className="text-ink-meta">{step.message}</dd> : null}
                  </dl>
                ) : null}

                {onChange ? (
                  <button
                    type="button"
                    data-remove-node={node.id}
                    aria-label={labels.removeStep(node.label)}
                    onClick={() => onChange(removeNode(graph, node.id))}
                    className="absolute end-1 top-1 text-ink-meta hover:text-danger-text"
                  >
                    <Icon name="X" size="inline" decorative />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function format(value: Record<string, unknown>): string {
  const entries = Object.entries(value);
  if (entries.length === 0) return '—';
  return entries.map(([key, entry]) => `${key}: ${stringify(entry)}`).join('\n');
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.map(stringify).join(', ');
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record['description'] === 'string') {
      return `${record['description']}${record['matched'] === false ? ' (no)' : ''}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function iconFor(palette: Palette, type: string): string {
  return palette.entries.find((entry) => entry.type === type)?.icon ?? 'Circle';
}

function canvasWidth(graph: FlowGraph): number {
  return Math.max(...graph.nodes.map((node) => node.x + NODE_WIDTH), 640) + 40;
}

function canvasHeight(graph: FlowGraph): number {
  return Math.max(...graph.nodes.map((node) => node.y + NODE_HEIGHT), 320) + 40;
}

/** Places a new node below the lowest one, joined to nothing. */
export function addNode(
  graph: FlowGraph,
  type: string,
  kind: FlowNode['kind'],
  label: string,
): FlowGraph {
  const id = `${type}-${String(graph.nodes.length + 1)}`;
  const y = graph.nodes.length === 0 ? 20 : Math.max(...graph.nodes.map((node) => node.y)) + 100;
  return {
    ...graph,
    nodes: [...graph.nodes, { id, kind, type, label, x: 40, y }],
  };
}

/** Removes a node and every edge touching it, so no edge dangles. */
export function removeNode(graph: FlowGraph, nodeId: string): FlowGraph {
  return {
    nodes: graph.nodes.filter((node) => node.id !== nodeId),
    edges: graph.edges.filter((edge) => edge.from !== nodeId && edge.to !== nodeId),
  };
}
